/**
 * Stream delta processing utilities
 */

import { accumulateToolCalls } from "./toolHandling";

// Track canvas triggers to prevent duplicates - with memory management
const MAX_CANVAS_TRIGGERS = 50; // Limit memory usage
let canvasTriggeredForStream = new Map(); // Use Map with timestamps for better cleanup

// ADDED: Grok-specific content buffering to handle tool call behavior
const grokContentBuffers = new Map(); // Map of messageId -> { content: string, hasToolCall: boolean, isBuffering: boolean }
const MAX_GROK_BUFFERS = 20; // Limit memory usage

/**
 * Clean up old canvas triggers to prevent memory leaks
 */
function cleanupCanvasTriggers() {
  if (canvasTriggeredForStream.size <= MAX_CANVAS_TRIGGERS) return;

  // Convert to array and sort by timestamp
  const entries = Array.from(canvasTriggeredForStream.entries()).sort(
    (a, b) => a[1] - b[1]
  ); // Sort by timestamp

  // Keep only the most recent triggers
  const toKeep = entries.slice(-MAX_CANVAS_TRIGGERS);
  canvasTriggeredForStream.clear();

  toKeep.forEach(([key, timestamp]) => {
    canvasTriggeredForStream.set(key, timestamp);
  });
}

/**
 * Clean up old Grok content buffers to prevent memory leaks
 */
function cleanupGrokBuffers() {
  if (grokContentBuffers.size <= MAX_GROK_BUFFERS) return;

  // Convert to array and sort by timestamp (assuming messageId contains timestamp)
  const entries = Array.from(grokContentBuffers.entries());

  // Keep only the most recent buffers
  const toKeep = entries.slice(-MAX_GROK_BUFFERS);
  grokContentBuffers.clear();

  toKeep.forEach(([key, value]) => {
    grokContentBuffers.set(key, value);
  });
}

/**
 * Check if provider is Grok/XAI
 * @param {string} modelProvider - The model provider name
 * @param {string} modelName - The model name
 * @returns {boolean} - Whether this is a Grok provider
 */
function isGrokProvider(modelProvider, modelName) {
  return (
    modelProvider === "xai" ||
    modelProvider === "grok" ||
    (modelName && (modelName.includes("grok") || modelName.includes("xai")))
  );
}

/**
 * Handle Grok-specific content buffering for tool calls
 * @param {string} deltaContent - Content from the delta
 * @param {string} modelProvider - The model provider name
 * @param {string} modelName - The model name
 * @param {string} messageId - Current message ID
 * @param {boolean} hasToolCallInDelta - Whether this delta contains tool calls
 * @param {boolean} hasToolsEnabled - Whether tools are enabled for this request
 * @returns {Object} - { shouldBufferContent: boolean, bufferedContent: string, finalContent: string }
 */
function handleGrokContentBuffering(
  deltaContent,
  modelProvider,
  modelName,
  messageId,
  hasToolCallInDelta,
  hasToolsEnabled = false
) {
  // Only buffer for Grok providers AND when tools are enabled
  // If tools are disabled, bypass buffering entirely for normal streaming
  if (!isGrokProvider(modelProvider, modelName) || !hasToolsEnabled) {
    return {
      shouldBufferContent: false,
      bufferedContent: "",
      finalContent: deltaContent,
    };
  }

  // Clean up old buffers periodically
  if (grokContentBuffers.size > MAX_GROK_BUFFERS) {
    cleanupGrokBuffers();
  }

  // Get or create buffer for this message
  let buffer = grokContentBuffers.get(messageId) || {
    content: "",
    hasToolCall: false,
    isBuffering: false,
  };

  // If we detect a tool call in this delta, mark it and discard all buffered content
  if (hasToolCallInDelta) {
    buffer.hasToolCall = true;
    buffer.isBuffering = false;

    // Store the updated buffer
    grokContentBuffers.set(messageId, buffer);

    // Return empty content since this is preliminary content before tool call
    return {
      shouldBufferContent: true,
      bufferedContent: buffer.content,
      finalContent: "", // Don't add the preliminary content to the message
    };
  }

  // If we haven't seen a tool call yet, buffer the content but don't display it yet
  if (!buffer.hasToolCall && deltaContent) {
    buffer.content += deltaContent;
    buffer.isBuffering = true;

    // Store the updated buffer
    grokContentBuffers.set(messageId, buffer);

    // For Grok, we buffer the content but don't display it until we know if there's a tool call
    // This prevents the preliminary "I'm happy to help..." content from appearing
    return {
      shouldBufferContent: true,
      bufferedContent: buffer.content,
      finalContent: "", // Don't display content until we know if this leads to a tool call
    };
  }

  // If we've already processed a tool call for this message, allow content through
  // (this would be follow-up content after tool execution)
  if (buffer.hasToolCall) {
    return {
      shouldBufferContent: false,
      bufferedContent: buffer.content,
      finalContent: deltaContent,
    };
  }

  // Default case - allow content through
  return {
    shouldBufferContent: false,
    bufferedContent: "",
    finalContent: deltaContent,
  };
}

/**
 * Clear Grok buffer for a message (call this when message is complete)
 * @param {string} messageId - Message ID to clear buffer for
 */
export function clearGrokBuffer(messageId) {
  if (grokContentBuffers.has(messageId)) {
    grokContentBuffers.delete(messageId);
  }
}

/**
 * Extract content from stream delta based on provider
 * @param {Object} delta - Stream delta object
 * @param {string} modelProvider - The model provider name
 * @returns {Object} - Extracted content object with content and reasoning_content
 */
export function extractDeltaContent(delta, modelProvider) {
  let deltaContent = "";
  let reasoningContent = "";

  // Handle backend SSE format (wrapped in chunk)
  if (delta.choices && delta.choices[0] && delta.choices[0].delta) {
    deltaContent = delta.choices[0].delta.content || "";
    reasoningContent = delta.choices[0].delta.reasoning_content || "";
  } else if (delta.delta?.text) {
    // Direct Anthropic format (legacy support)
    deltaContent = delta.delta.text || "";
  } else if (delta.content) {
    // Direct content field
    deltaContent = delta.content || "";
  }

  return {
    content: deltaContent,
    reasoning_content: reasoningContent,
  };
}

/**
 * Extract citations from delta metadata
 * @param {Object} delta - Stream delta object
 * @returns {Array|null} - Citations array or null
 */
export function extractCitations(delta) {
  // Handle backend format with citations in metadata
  if (delta.choices && delta.choices[0]?.delta?.metadata) {
    const metadata = delta.choices[0].delta.metadata;
    if (metadata.citations && Array.isArray(metadata.citations)) {
      console.log(
        "[extractCitations] Found citations in delta metadata:",
        metadata.citations.length,
        "sources"
      );
      return metadata.citations;
    }
    if (metadata.hasCitations) {
      console.log(
        "[extractCitations] hasCitations flag found but no citations array"
      );
    }
  }

  // Handle direct citations field
  if (delta.citations && Array.isArray(delta.citations)) {
    console.log(
      "[extractCitations] Found citations in delta.citations:",
      delta.citations.length,
      "sources"
    );
    return delta.citations;
  }

  return null;
}

/**
 * Extract reasoning metadata from delta
 * @param {Object} delta - Stream delta object
 * @returns {Object|null} - Reasoning metadata or null
 */
export function extractReasoningMetadata(delta) {
  // Handle backend format with reasoning completion
  if (delta.choices && delta.choices[0]?.delta?.metadata) {
    const metadata = delta.choices[0].delta.metadata;

    // Check for reasoning completion signal
    if (metadata.reasoning_complete) {
      return {
        reasoning_complete: true,
        reasoning_token_count: metadata.reasoning_token_count,
        reasoning_seconds: metadata.reasoning_seconds,
        reasoning_transition: metadata.reasoning_transition,
      };
    }

    // ADDED: Handle reasoning transition signal specifically
    if (metadata.reasoning_transition) {
      return {
        reasoning_transition: true,
        reasoning_complete: true,
      };
    }

    // Handle regular reasoning metadata
    if (metadata.reasoning_token_count) {
      return {
        reasoning_token_count: metadata.reasoning_token_count,
        reasoning_seconds: metadata.reasoning_seconds,
      };
    }
  }

  // Handle direct format
  if (delta.metadata?.reasoning_token_count) {
    return {
      reasoning_token_count: delta.metadata.reasoning_token_count,
      reasoning_seconds: delta.metadata.reasoning_seconds,
    };
  }

  return null;
}

/**
 * Process reasoning content for specific providers
 * @param {string} deltaContent - Delta content
 * @param {string} reasoningDeltaContent - Delta reasoning content
 * @param {string} modelProvider - The model provider name
 * @param {Object} reasoningState - Current reasoning state
 * @returns {Object} - Updated reasoning state
 */
export function processReasoningContent(
  deltaContent,
  reasoningDeltaContent,
  modelProvider,
  reasoningState
) {
  let {
    isReasoningResponse,
    reasoningContent,
    inReasoningBlock,
    reasoningStartTime,
    reasoningDuration,
  } = reasoningState;

  // Handle DeepSeek reasoning content directly from reasoning_content field
  if (modelProvider === "deepseek" && reasoningDeltaContent) {
    if (!isReasoningResponse) {
      isReasoningResponse = true;
      inReasoningBlock = true;
      reasoningStartTime = Date.now(); // Start timing when reasoning begins
    }
    reasoningContent += reasoningDeltaContent;
  }
  // CRITICAL FIX: For DeepSeek, if we had reasoning content before but now we don't,
  // it means reasoning is complete and we should not process regular content as reasoning
  else if (
    modelProvider === "deepseek" &&
    !reasoningDeltaContent &&
    isReasoningResponse
  ) {
    // If we were in a reasoning response but now have no reasoning content,
    // mark reasoning as complete and don't process regular content as reasoning
    if (inReasoningBlock) {
      inReasoningBlock = false;
      if (reasoningStartTime) {
        reasoningDuration = Math.round(
          (Date.now() - reasoningStartTime) / 1000
        ); // Duration in seconds
      }
    }
    // Don't process regular deltaContent as reasoning content for DeepSeek
  }
  // Handle XML-based reasoning content for other providers
  else if (typeof deltaContent === "string") {
    // Handle DeepSeek reasoning with <thinking> tags (legacy support only)
    if (modelProvider === "deepseek") {
      // ONLY process XML tags if we haven't already processed reasoning_content
      // This prevents regular content from being treated as reasoning
      if (!isReasoningResponse) {
        if (deltaContent.includes("<thinking>") && !inReasoningBlock) {
          inReasoningBlock = true;
          isReasoningResponse = true;
          reasoningStartTime = Date.now(); // Start timing when reasoning begins
        }

        if (deltaContent.includes("</thinking>") && inReasoningBlock) {
          inReasoningBlock = false;
          if (reasoningStartTime) {
            reasoningDuration = Math.round(
              (Date.now() - reasoningStartTime) / 1000
            ); // Duration in seconds
          }
        }

        if (inReasoningBlock) {
          reasoningContent += deltaContent;
        }
      }
      // If we already processed reasoning_content, don't process regular content as reasoning
    }
    // Handle Perplexity reasoning with <think> tags
    else if (modelProvider === "perplexity") {
      if (deltaContent.includes("<think>") && !inReasoningBlock) {
        inReasoningBlock = true;
        isReasoningResponse = true;
        reasoningStartTime = Date.now(); // Start timing when reasoning begins
      }

      if (deltaContent.includes("</think>") && inReasoningBlock) {
        inReasoningBlock = false;
        if (reasoningStartTime) {
          reasoningDuration = Math.round(
            (Date.now() - reasoningStartTime) / 1000
          ); // Duration in seconds
        }
      }

      if (inReasoningBlock) {
        reasoningContent += deltaContent;
      }
    }
  }

  return {
    isReasoningResponse,
    reasoningContent,
    inReasoningBlock,
    reasoningStartTime,
    reasoningDuration,
  };
}

/**
 * Check if a tool call is a canvas tool
 * @param {Object} toolCall - Tool call object
 * @returns {boolean} - Whether it's a canvas tool
 */
export function isCanvasTool(toolCall) {
  if (!toolCall?.function?.name) return false;

  const toolName = toolCall.function.name;
  return (
    toolName === "canvas_create" ||
    toolName === "canvas_edit" ||
    toolName.startsWith("canvas_")
  );
}

/**
 * Trigger canvas opening when canvas tool is detected
 * @param {Array} toolCalls - Array of tool calls
 * @param {string} streamId - Optional stream identifier to prevent duplicates
 * @returns {boolean} - Whether canvas was triggered
 */
export function triggerCanvasIfNeeded(toolCalls, streamId = null) {
  const canvasTools = toolCalls.filter(isCanvasTool);

  if (canvasTools.length === 0) {
    return false;
  }

  // Create a unique identifier for this trigger
  const triggerId =
    streamId ||
    `${Date.now()}_${canvasTools.map((t) => t.function?.name || "unknown").join("_")}`;

  // Check if already triggered (with timestamp)
  if (canvasTriggeredForStream.has(triggerId)) {
    return false;
  }

  try {
    // Mark as triggered with timestamp
    const now = Date.now();
    canvasTriggeredForStream.set(triggerId, now);

    // Clean up old triggers periodically
    if (canvasTriggeredForStream.size > MAX_CANVAS_TRIGGERS) {
      cleanupCanvasTriggers();
    }

    // Dispatch event to open canvas sidebar immediately
    const openSidebarEvent = new CustomEvent("open-canvas-sidebar", {
      detail: {
        source: "canvas-tool-detected-in-stream",
        timestamp: new Date().toISOString(),
        toolNames: canvasTools.map((t) => t.function?.name),
        triggerId,
      },
    });
    window.dispatchEvent(openSidebarEvent);

    // Also emit canvas event for the CanvasContext
    import("@/lib/ai/tools/canvasStreamingTool")
      .then(({ canvasEventEmitter }) => {
        canvasEventEmitter.emit("canvas_create", {
          content: "",
          type: "text",
          language: "javascript",
          metadata: {
            source: "tool_call_detected",
            isStreaming: true,
            createdAt: new Date().toISOString(),
            toolNames: canvasTools.map((t) => t.function?.name),
            triggerId,
          },
        });
      })
      .catch((error) => {
        // Could not emit canvas event
      });

    return true;
  } catch (error) {
    // Could not trigger canvas opening
    // Remove from triggered set if there was an error
    canvasTriggeredForStream.delete(triggerId);
  }

  return false;
}

/**
 * Process tool calls from delta (Backend Compatible)
 * @param {Object} delta - Stream delta object
 * @param {Array} pendingToolCalls - Current pending tool calls
 * @param {string} modelProvider - The model provider name
 * @returns {Object} - Processing result with updated tool calls and flags
 */
export function processToolCallsFromDelta(
  delta,
  pendingToolCalls,
  modelProvider
) {
  let hasDetectedToolCall = false;
  let bufferingForToolCall = false;
  let updatedPendingToolCalls = [...pendingToolCalls];
  let canvasTriggered = false;

  // Log delta structure for debugging image_gen
  if (delta?.choices?.[0]?.delta?.tool_calls) {
    const toolCalls = delta.choices[0].delta.tool_calls || [];
    const imageGenCalls = toolCalls.filter(
      (tc) => tc.function?.name === "image_gen"
    );
  }

  // Handle backend SSE format
  if (delta.choices && delta.choices[0]?.delta?.tool_calls) {
    bufferingForToolCall = true;
    hasDetectedToolCall = true;

    const incomingToolCalls = delta.choices[0].delta.tool_calls || [];

    // The backend now provides properly formatted tool calls for all providers
    // including Anthropic, so we can process them uniformly
    const existingIds = new Set(
      updatedPendingToolCalls.map((tc) => tc.id).filter(Boolean)
    );

    for (const toolCall of incomingToolCalls) {
      // Ensure tool call has an ID
      if (!toolCall.id) {
        if (modelProvider === "mistral") {
          const alphanumeric =
            "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
          let mistralId = "";
          for (let i = 0; i < 9; i++) {
            mistralId += alphanumeric.charAt(
              Math.floor(Math.random() * alphanumeric.length)
            );
          }
          toolCall.id = mistralId;
        } else {
          toolCall.id = `call_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
        }
      } else if (existingIds.has(toolCall.id)) {
        // Handle duplicate IDs for Mistral
        if (modelProvider === "mistral") {
          const alphanumeric =
            "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
          let mistralId = "";
          for (let i = 0; i < 9; i++) {
            mistralId += alphanumeric.charAt(
              Math.floor(Math.random() * alphanumeric.length)
            );
          }
          toolCall.id = mistralId;
        }
      }
    }

    // Use the improved accumulation function
    updatedPendingToolCalls = accumulateToolCalls(
      updatedPendingToolCalls,
      incomingToolCalls,
      modelProvider
    );

    // Check for canvas tools and trigger canvas opening immediately
    const allToolCalls = [...updatedPendingToolCalls];
    // Create a unique stream identifier based on the tool calls to prevent duplicates
    const streamId = allToolCalls
      .map((tc) => `${tc.id || "unknown"}_${tc.function?.name || "unknown"}`)
      .join("|");
    canvasTriggered = triggerCanvasIfNeeded(allToolCalls, streamId);
  }

  return {
    hasDetectedToolCall,
    bufferingForToolCall,
    pendingToolCalls: updatedPendingToolCalls,
    canvasTriggered,
  };
}

/**
 * Check if tool calls are complete
 * @param {Array} pendingToolCalls - Pending tool calls array
 * @returns {boolean} - Whether tool calls are complete
 */
export function areToolCallsComplete(pendingToolCalls) {
  if (pendingToolCalls.length === 0) return false;

  return pendingToolCalls.some((tc) => {
    if (!tc.function?.arguments) return false;
    try {
      JSON.parse(tc.function.arguments);
      return true;
    } catch (e) {
      return false;
    }
  });
}

/**
 * Process stream delta and update state
 * @param {Object} params - Processing parameters
 * @returns {Object} - Updated state
 */
export function processStreamDelta({
  delta,
  modelProvider,
  modelName,
  messageId,
  reasoningState,
  pendingToolCalls,
  bufferingForToolCall,
  hasDetectedToolCall,
  toolCallsFullyAccumulated,
  hasToolsEnabled = false,
}) {
  // Extract content and reasoning content
  const { content: deltaContent, reasoning_content: reasoningDeltaContent } =
    extractDeltaContent(delta, modelProvider);

  // Extract citations from metadata
  const citations = extractCitations(delta);

  // Extract reasoning metadata
  const reasoningMetadata = extractReasoningMetadata(delta);

  // Process tool calls first to detect if this delta contains tool calls
  const toolCallResult = processToolCallsFromDelta(
    delta,
    pendingToolCalls,
    modelProvider
  );

  // ADDED: Handle Grok-specific content buffering
  // Only buffer when tools are actually enabled to prevent buffering normal text responses
  const grokBufferResult = handleGrokContentBuffering(
    deltaContent,
    modelProvider,
    modelName,
    messageId,
    toolCallResult.hasDetectedToolCall,
    hasToolsEnabled
  );

  // Use the processed content from Grok handler
  const processedDeltaContent = grokBufferResult.finalContent;

  // Process reasoning content with the processed content
  let updatedReasoningState = processReasoningContent(
    processedDeltaContent,
    reasoningDeltaContent,
    modelProvider,
    reasoningState
  );

  // Handle reasoning completion from backend metadata
  if (
    reasoningMetadata?.reasoning_complete &&
    reasoningMetadata?.reasoning_seconds
  ) {
    updatedReasoningState = {
      ...updatedReasoningState,
      reasoningDuration: reasoningMetadata.reasoning_seconds,
      inReasoningBlock: false, // Mark reasoning as complete
    };
  }

  // ADDED: Handle reasoning transition metadata (DeepSeek-specific)
  if (
    reasoningMetadata?.reasoning_transition ||
    reasoningMetadata?.reasoning_complete
  ) {
    updatedReasoningState = {
      ...updatedReasoningState,
      inReasoningBlock: false, // Force reasoning block to close
      reasoningDuration:
        reasoningMetadata.reasoning_seconds ||
        updatedReasoningState.reasoningDuration,
    };
  }

  // Check if stream is completed
  let streamCompleted = false;
  let grokReleasedContent = "";

  if (delta.choices && delta.choices[0] && delta.choices[0].finish_reason) {
    streamCompleted = true;

    // ADDED: Handle Grok buffer when stream completes
    if (isGrokProvider(modelProvider, modelName) && messageId) {
      const buffer = grokContentBuffers.get(messageId);

      // If stream completes without tool calls but we have buffered content, release it
      if (
        buffer &&
        buffer.isBuffering &&
        !buffer.hasToolCall &&
        buffer.content
      ) {
        grokReleasedContent = buffer.content;
      }

      clearGrokBuffer(messageId);
    }

    if (delta.choices[0].finish_reason === "tool_calls") {
      // Ensure hasDetectedToolCall is set for tool_calls finish reason
      hasDetectedToolCall = true;
      toolCallsFullyAccumulated = true;

      // Also ensure we have tool calls in pending array
      if (
        toolCallResult.pendingToolCalls.length === 0 &&
        delta.choices[0].delta?.tool_calls
      ) {
        // Force add tool calls if they weren't captured in toolCallResult
        const toolCallsFromFinish = delta.choices[0].delta.tool_calls.map(
          (tc) => ({
            ...tc,
            id:
              tc.id ||
              `call_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
            type: tc.type || "function",
            isStreaming: false,
          })
        );
        toolCallResult.pendingToolCalls = [
          ...toolCallResult.pendingToolCalls,
          ...toolCallsFromFinish,
        ];
      }
    }
  }

  return {
    deltaContent: grokReleasedContent || processedDeltaContent, // Return released content if available, otherwise processed content
    reasoningDeltaContent,
    reasoningMetadata,
    citations: citations, // Include citations if present
    reasoningState: updatedReasoningState,
    hasDetectedToolCall:
      toolCallResult.hasDetectedToolCall || hasDetectedToolCall,
    bufferingForToolCall:
      toolCallResult.bufferingForToolCall || bufferingForToolCall,
    pendingToolCalls: toolCallResult.pendingToolCalls,
    toolCallsFullyAccumulated,
    streamCompleted,
    canvasTriggered: toolCallResult.canvasTriggered || false,
    // ADDED: Include Grok buffering info in return
    grokBuffering: {
      isGrokProvider: isGrokProvider(modelProvider, modelName),
      shouldBuffer: grokBufferResult.shouldBufferContent,
      bufferedContent: grokBufferResult.bufferedContent,
      originalContent: deltaContent,
      releasedContent: grokReleasedContent, // Include released content for debugging
    },
  };
}
